import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@artosera/ui';
import styles from './AccesApiPage.module.css';
import { useTranslation } from 'react-i18next';
import {
  telechargerFactureAddon,
  useFacturesAddons,
  usePaiementAddon,
  useSimulationPaiementAddon,
} from '../../api/addons.js';
import { enregistrerFichier, naviguerVersPaiement } from '../../api/client.js';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
export function FacturesAddons() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const requete = useFacturesAddons(galerieSlug, galerieALaction(galerie, 'addons:invoices'));
  const [params] = useSearchParams();
  const paiement = usePaiementAddon(galerieSlug);
  const simulation = useSimulationPaiementAddon(galerieSlug);
  const cles = useRef(new Map<string, string>());
  const [erreurPdf, setErreurPdf] = useState(false);
  async function telecharger(id: string, numero: string) {
    setErreurPdf(false);
    try {
      const fichier = await telechargerFactureAddon(galerieSlug, id);
      enregistrerFichier(fichier, `${numero}.pdf`);
    } catch {
      setErreurPdf(true);
    }
  }
  async function payer(id: string) {
    let cle = cles.current.get(id);
    if (!cle) {
      cle = crypto.randomUUID();
      cles.current.set(id, cle);
    }
    const session = await paiement.mutateAsync({ factureId: id, cle });
    naviguerVersPaiement(session.url);
  }
  async function simuler(issue: 'payee' | 'echouee' | 'abandonnee') {
    const retour = await simulation.mutateAsync({
      factureId: params.get('facture')!,
      tentativeId: params.get('tentative')!,
      issue,
    });
    naviguerVersPaiement(retour.url);
  }
  const prix = (n: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(n / 100);
  return (
    <section className={styles.section}>
      <h2>{t('reglages.facturesAddons.titre')}</h2>
      {params.get('paiement') === 'retour' ? (
        <p role="status">{t('reglages.facturesAddons.verification')}</p>
      ) : null}
      {paiement.isError || simulation.isError ? (
        <p role="alert">{t('reglages.facturesAddons.erreurPaiement')}</p>
      ) : null}
      {requete.data?.paiementEnLigneDisponible &&
      params.get('paiement') === 'simulation' &&
      params.get('facture') &&
      params.get('tentative') ? (
        <section>
          <h3>{t('reglages.facturesAddons.simulation')}</h3>
          {(['payee', 'echouee', 'abandonnee'] as const).map((issue) => (
            <Button
              key={issue}
              disabled={simulation.isPending}
              onClick={() => {
                void simuler(issue).catch(() => {});
              }}
            >
              {t(`reglages.facturesAddons.simuler_${issue}`)}
            </Button>
          ))}
        </section>
      ) : null}
      {requete.isPending ? <p role="status">{t('reglages.facturesAddons.chargement')}</p> : null}
      {requete.isError ? <p role="alert">{t('reglages.facturesAddons.erreur')}</p> : null}
      {erreurPdf ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {requete.data ? (
        <>
          <p>{t('reglages.facturesAddons.virement')}</p>
          <dl>
            <dt>IBAN</dt>
            <dd>{requete.data.virement.iban ?? '—'}</dd>
            <dt>BIC</dt>
            <dd>{requete.data.virement.bic ?? '—'}</dd>
            <dt>{t('reglages.facturesAddons.titulaire')}</dt>
            <dd>{requete.data.virement.titulaire ?? '—'}</dd>
          </dl>
          <table className={styles.table} data-ui="data-table">
            <thead>
              <tr>
                {['numero', 'date', 'echeance', 'ttc', 'regle', 'etat', 'reference', 'pdf'].map(
                  (c) => (
                    <th key={c}>{t(`reglages.facturesAddons.${c}`)}</th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {requete.data.items.map((facture) => (
                <tr key={facture.id}>
                  <td>
                    {facture.numero}
                    {facture.estTest ? ` · ${t('reglages.facturesAddons.specimen')}` : ''}
                    <ul>
                      {facture.avoirs.map((a) => (
                        <li key={a.id}>
                          {a.pdfDisponible ? (
                            <Button
                              onClick={() => {
                                void telecharger(a.id, a.numero);
                              }}
                            >
                              {t('reglages.facturesAddons.avoir')} {a.numero} ·{' '}
                              {prix(a.ttcCentimes)}
                            </Button>
                          ) : (
                            `${t('reglages.facturesAddons.avoir')} ${a.numero} · ${prix(a.ttcCentimes)} · ${t('reglages.facturesAddons.preparation')}`
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>{facture.dateEmission}</td>
                  <td>{facture.echeance}</td>
                  <td>{prix(facture.ttcCentimes)}</td>
                  <td>{prix(facture.regleCentimes)}</td>
                  <td>{t(`reglages.facturesAddons.${facture.etat}`)}</td>
                  <td>
                    {facture.numero}
                    {requete.data.paiementEnLigneDisponible &&
                    facture.resteDuTtcCentimes > 0 &&
                    (facture.etat === 'a_payer' || facture.etat === 'en_retard') ? (
                      <Button
                        disabled={paiement.isPending}
                        onClick={() => {
                          void payer(facture.id).catch(() => {});
                        }}
                      >
                        {t('reglages.facturesAddons.payer')}
                      </Button>
                    ) : null}
                  </td>
                  <td>
                    {facture.pdfDisponible ? (
                      <Button
                        onClick={() => {
                          void telecharger(facture.id, facture.numero);
                        }}
                      >
                        {t('reglages.facturesAddons.telecharger')}
                      </Button>
                    ) : (
                      t('reglages.facturesAddons.preparation')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {requete.data.items.length === 0 ? <p>{t('reglages.facturesAddons.vide')}</p> : null}
        </>
      ) : null}
    </section>
  );
}
